import { App } from "@capacitor/app";
import { Device } from "@capacitor/device";
import { collection, doc, getDoc, getDocs, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db } from "../firebase/client";
import { platform } from "../utils/platform";
import type { DeviceSession } from "../types/domain";

let cachedDeviceId: string | null = null;

export async function currentDeviceId(): Promise<string> {
  if (cachedDeviceId) return cachedDeviceId;
  const id = await Device.getId();
  cachedDeviceId = id.identifier;
  return cachedDeviceId;
}

function webDeviceName(): string | undefined {
  if (typeof navigator === "undefined") return undefined;
  const ua = navigator.userAgent || "";
  const browser = ua.includes("Edg/") ? "Microsoft Edge"
    : ua.includes("Chrome/") ? "Google Chrome"
      : ua.includes("Firefox/") ? "Mozilla Firefox"
        : ua.includes("Safari/") ? "Safari"
          : undefined;
  const os = ua.includes("Windows") ? "Windows"
    : ua.includes("Mac OS") ? "macOS"
      : ua.includes("Android") ? "Android"
        : /iPhone|iPad|iPod/.test(ua) ? "iOS"
          : undefined;
  return [browser, os].filter(Boolean).join(" · ") || undefined;
}

export async function registerCurrentDevice(uid: string): Promise<string> {
  if (auth.currentUser?.uid !== uid) throw new Error("Inactive device account");
  const [id, info] = await Promise.all([currentDeviceId(), Device.getInfo()]);
  const ref = doc(db, "users", uid, "devices", id);
  const existing = await getDoc(ref);
  if (auth.currentUser?.uid !== uid) throw new Error("Inactive device account");
  if (existing.data()?.status === "revoked") throw new Error("Device session revoked");
  const currentPlatform = platform();
  const installed = currentPlatform === "web" ? null : await App.getInfo().catch(() => null);
  const appVersion = currentPlatform === "web" ? String(import.meta.env.VITE_APP_VERSION || "").trim() : installed?.version;
  if (auth.currentUser?.uid !== uid) throw new Error("Inactive device account");
  const now = new Date().toISOString();

  // Firestore rejects `undefined` field values by default. Build the payload
  // only with values that are actually available on the current platform.
  const payload: Record<string, unknown> = {
    id,
    platform: currentPlatform,
    status: "active",
    lastSeenAt: now,
    updatedAt: serverTimestamp()
  };

  if (appVersion) payload.appVersion = appVersion;
  if (installed?.build) payload.appBuild = installed.build;
  if (!existing.exists()) payload.createdAt = serverTimestamp();

  const deviceName = currentPlatform === "web"
    ? webDeviceName() || info.name || info.model
    : info.name || info.model;

  if (deviceName) payload.deviceName = deviceName;
  if (info.model) payload.model = info.model;
  if (info.operatingSystem) payload.operatingSystem = info.operatingSystem;
  if (info.osVersion) payload.osVersion = info.osVersion;

  await setDoc(ref, payload, { merge: true });
  return id;
}

export async function listDeviceSessions(uid: string): Promise<DeviceSession[]> {
  const current = await currentDeviceId();
  const snapshot = await getDocs(collection(db, "users", uid, "devices"));
  return snapshot.docs.map((item) => ({
    ...(item.data() as DeviceSession),
    id: item.id,
    current: item.id === current
  })).sort((a, b) => String(b.lastSeenAt || "").localeCompare(String(a.lastSeenAt || "")));
}

export async function revokeDevice(uid: string, deviceId: string): Promise<void> {
  await setDoc(doc(db, "users", uid, "devices", deviceId), {
    status: "revoked",
    revokedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  }, { merge: true });
}

export async function revokeOtherDevices(uid: string): Promise<void> {
  const current = await currentDeviceId();
  const devices = await listDeviceSessions(uid);
  await Promise.all(devices.filter((item) => item.id !== current && item.status !== "revoked")
    .map((item) => revokeDevice(uid, item.id)));
}

export async function watchCurrentDeviceRevocation(uid: string, onRevoked: () => void): Promise<() => void> {
  const id = await currentDeviceId();
  return onSnapshot(doc(db, "users", uid, "devices", id), (snapshot) => {
    if (snapshot.exists() && snapshot.data()?.status === "revoked") onRevoked();
  });
}
